import React, { useState, useEffect } from 'react';
import { 
  collection, 
  addDoc, 
  Timestamp, 
  onSnapshot, 
  query, 
  where,
  orderBy,
  getDocs,
  limit
} from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { useAuth } from '../App';
import { 
  X, 
  Plus, 
  Trash2, 
  Loader2, 
  DollarSign, 
  Calendar, 
  Building2, 
  FileText,
  User,
  Hash,
  MapPin
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Client, Branch, UserProfile, CashRequisitionItem, OfficeLocation } from '../types';
import { toast } from 'sonner';
import { downloadFile } from '../lib/downloadHelper';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

interface CashRequisitionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CashRequisitionModal: React.FC<CashRequisitionModalProps> = ({ isOpen, onClose }) => {
  const { profile } = useAuth();
  const [locations, setLocations] = useState<OfficeLocation[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [staff, setStaff] = useState<UserProfile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [letterheadUrl, setLetterheadUrl] = useState<string | null>(null);

  const [cashFormData, setCashFormData] = useState({
    siteName: '',
    branchId: '',
    department: profile?.department || '',
    requisitionDate: new Date().toISOString().split('T')[0],
    jobCardNo: '',
    requestedBy: profile?.displayName || '',
    requestedById: profile?.uid || '',
    items: [{ purpose: '', quantity: 1, rate: 0, amount: 0 }] as CashRequisitionItem[]
  });

  useEffect(() => {
    if (!isOpen) return;

    const locationsUnsubscribe = onSnapshot(query(collection(db, 'office_locations'), orderBy('name', 'asc')), (snapshot) => {
      setLocations(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as OfficeLocation)));
    });

    const staffUnsubscribe = onSnapshot(collection(db, 'users'), (snapshot) => {
      setStaff(snapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() } as UserProfile)));
    });

    const settingsUnsubscribe = onSnapshot(collection(db, 'settings'), (snapshot) => {
      if (!snapshot.empty) {
        setLetterheadUrl(snapshot.docs[0].data().letterheadUrl || null);
      }
    });

    return () => {
      locationsUnsubscribe();
      staffUnsubscribe();
      settingsUnsubscribe();
    };
  }, [isOpen]);

  useEffect(() => {
    if (cashFormData.siteName) {
      const location = locations.find(l => l.name === cashFormData.siteName);
      if (location) {
        setBranches(location.branches || []);
      }
    } else {
      setBranches([]);
    }
  }, [cashFormData.siteName, locations]);

  const handleAddItem = () => {
    setCashFormData({
      ...cashFormData,
      items: [...cashFormData.items, { purpose: '', quantity: 1, rate: 0, amount: 0 }]
    });
  };

  const handleRemoveItem = (index: number) => {
    setCashFormData({
      ...cashFormData,
      items: cashFormData.items.filter((_, i) => i !== index)
    });
  };

  const handleItemChange = (index: number, field: keyof CashRequisitionItem, value: any) => {
    const newItems = [...cashFormData.items];
    
    // Handle numeric inputs to avoid NaN
    let processedValue = value;
    if (field === 'quantity') {
      processedValue = parseInt(value) || 0;
    } else if (field === 'rate') {
      processedValue = parseFloat(value) || 0;
    }
    
    newItems[index] = { ...newItems[index], [field]: processedValue };
    
    if (field === 'quantity' || field === 'rate') {
      newItems[index].amount = (newItems[index].quantity || 0) * (newItems[index].rate || 0);
    }
    
    setCashFormData({ ...cashFormData, items: newItems });
  };

  const generateCodeNo = async () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    
    try {
      // Get the absolute latest requisition to continue the sequence
      const q = query(
        collection(db, 'cash_requisitions'),
        orderBy('createdAt', 'desc'),
        limit(1)
      );
      
      const querySnapshot = await getDocs(q);
      let lastNumber = 860; // Starting point as per user example (861 will be next)
      
      if (!querySnapshot.empty) {
        const lastDoc = querySnapshot.docs[0].data();
        const lastCode = lastDoc.codeNo;
        if (lastCode && typeof lastCode === 'string') {
          const parts = lastCode.split('/');
          const numPart = parseInt(parts[parts.length - 1]);
          if (!isNaN(numPart)) {
            lastNumber = numPart;
          }
        }
      }
      
      const nextNumber = lastNumber + 1;
      const sequenceStr = nextNumber.toString().padStart(3, '0');
      return `BTSGSL/FIN/${year}/${month}/${sequenceStr}`;
    } catch (error) {
      console.error("Error generating code no:", error);
      // Fallback to a timestamp-based unique number if query fails
      const timestampPart = Date.now().toString().slice(-3);
      return `BTSGSL/FIN/${year}/${month}/${timestampPart}`;
    }
  };

  const generatePDF = (reqData: any) => {
    const doc = new jsPDF();
    const initials = profile?.displayName?.split(' ').map(n => n[0]).join('').toUpperCase() || '';

    // Letterhead
    if (letterheadUrl) {
      doc.addImage(letterheadUrl, 'PNG', 10, 5, 190, 35);
    }

    let currentY = 50;
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('CASH REQUISITION NOTE', 105, currentY, { align: 'center' });
    
    currentY += 15;
    doc.setFontSize(10);
    doc.text('Head Office', 20, currentY);
    doc.text(`Code No: ${reqData.codeNo}`, 140, currentY);

    currentY += 10;
    doc.text(`Site Name: ${reqData.siteName}`, 20, currentY);
    doc.text(`Requisition Date: ${new Date(reqData.requisitionDate).toLocaleDateString()}`, 140, currentY);

    currentY += 10;
    doc.text(`Job Card No: ${reqData.jobCardNo || 'N/A'}`, 20, currentY);

    currentY += 10;
    doc.text(`Requested by: ${reqData.requestedBy}`, 20, currentY);
    doc.text(`Signature: ${initials}`, 140, currentY);

    currentY += 15;
    const tableData = reqData.items.map((item: any, i: number) => [
      i + 1,
      item.purpose,
      item.quantity,
      item.rate.toLocaleString(),
      item.amount.toLocaleString()
    ]);

    autoTable(doc, {
      startY: currentY,
      head: [['S/N', 'Purpose of Requisition', 'Qty', 'Rate (TZS)', 'Amount (TZS)']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [30, 41, 59] },
      foot: [['', '', '', 'Total Requested Amount', reqData.totalAmount.toLocaleString()]],
      footStyles: { fillColor: [241, 245, 249], textColor: [30, 41, 59], fontStyle: 'bold' }
    });

    downloadFile(doc.output('blob'), `Cash_Requisition_${reqData.codeNo.replace(/\//g, '_')}.pdf`);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const codeNo = await generateCodeNo();
      const totalAmount = cashFormData.items.reduce((sum, item) => sum + item.amount, 0);
      
      const reqData = {
        codeNo,
        siteName: cashFormData.siteName,
        branchId: cashFormData.branchId,
        branchName: branches.find(b => b.id === cashFormData.branchId)?.name || '',
        department: cashFormData.department,
        requisitionDate: Timestamp.fromDate(new Date(cashFormData.requisitionDate)),
        jobCardNo: cashFormData.jobCardNo,
        requestedBy: cashFormData.requestedBy,
        requestedById: cashFormData.requestedById,
        signatureInitials: profile?.displayName?.split(' ').map(n => n[0]).join('').toUpperCase() || '',
        items: cashFormData.items,
        totalAmount,
        status: 'pending_hod',
        currentStep: 0,
        approvals: [],
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      await addDoc(collection(db, 'cash_requisitions'), reqData);
      
      generatePDF({ ...cashFormData, codeNo, totalAmount });
      
      toast.success('Cash requisition submitted successfully');
      onClose();
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'cash_requisitions');
      toast.error('Failed to submit requisition');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative w-full max-w-4xl bg-white rounded-[2.5rem] shadow-2xl p-8 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600">
                  <DollarSign className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-3xl font-black text-slate-900 tracking-tight">Cash Requisition</h2>
                  <p className="text-slate-500 font-medium italic">Request funds for operational expenses</p>
                </div>
              </div>
              <button onClick={onClose} className="p-3 hover:bg-slate-100 rounded-2xl transition-colors">
                <X className="w-6 h-6 text-slate-400" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Office Location *</label>
                  <div className="relative">
                    <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <select
                      required
                      value={cashFormData.siteName}
                      onChange={(e) => setCashFormData({ ...cashFormData, siteName: e.target.value, branchId: '' })}
                      className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500/10 transition-all font-bold"
                    >
                      <option value="">Select Office</option>
                      {locations.map(l => <option key={l.id} value={l.name}>{l.name}</option>)}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Branch / Location</label>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <select
                      value={cashFormData.branchId}
                      onChange={(e) => setCashFormData({ ...cashFormData, branchId: e.target.value })}
                      className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500/10 transition-all font-bold"
                    >
                      <option value="">Main Office / Select Branch</option>
                      {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Department *</label>
                  <input
                    type="text"
                    required
                    value={cashFormData.department}
                    onChange={(e) => setCashFormData({ ...cashFormData, department: e.target.value })}
                    placeholder="e.g. Technical, Finance"
                    className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500/10 transition-all font-bold"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Requisition Date *</label>
                  <div className="relative">
                    <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="date"
                      required
                      value={cashFormData.requisitionDate}
                      onChange={(e) => setCashFormData({ ...cashFormData, requisitionDate: e.target.value })}
                      className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500/10 transition-all font-bold"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Job Card No</label>
                  <div className="relative">
                    <Hash className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      value={cashFormData.jobCardNo}
                      onChange={(e) => setCashFormData({ ...cashFormData, jobCardNo: e.target.value })}
                      placeholder="Enter Job Card No"
                      className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500/10 transition-all font-bold"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Requested By *</label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <select
                      required
                      value={cashFormData.requestedById}
                      onChange={(e) => {
                        const s = staff.find(u => u.uid === e.target.value);
                        setCashFormData({ ...cashFormData, requestedById: e.target.value, requestedBy: s?.displayName || '' });
                      }}
                      className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500/10 transition-all font-bold"
                    >
                      {staff.map(u => <option key={u.uid} value={u.uid}>{u.displayName}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-black text-slate-900 tracking-tight">Requisition Items</h3>
                  <button
                    type="button"
                    onClick={handleAddItem}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-600 rounded-xl font-bold hover:bg-emerald-100 transition-all"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Item</span>
                  </button>
                </div>

                <div className="space-y-3">
                  {cashFormData.items.map((item, index) => (
                    <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end bg-slate-50 p-4 rounded-2xl border border-slate-100">
                      <div className="md:col-span-5 space-y-1">
                        <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Purpose</label>
                        <input
                          type="text"
                          required
                          value={item.purpose}
                          onChange={(e) => handleItemChange(index, 'purpose', e.target.value)}
                          placeholder="Purpose of requisition"
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl focus:outline-none text-xs font-bold"
                        />
                      </div>
                      <div className="md:col-span-2 space-y-1">
                        <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Qty</label>
                          <input
                            type="number"
                            required
                            min="1"
                            value={item.quantity || ''}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl focus:outline-none text-xs font-bold"
                          />
                        </div>
                        <div className="md:col-span-2 space-y-1">
                          <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Rate (TZS)</label>
                          <input
                            type="number"
                            required
                            value={item.rate || ''}
                            onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl focus:outline-none text-xs font-bold"
                          />
                      </div>
                      <div className="md:col-span-2 space-y-1">
                        <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Amount</label>
                        <div className="px-3 py-2 bg-slate-100 border border-slate-200 rounded-xl text-xs font-black text-slate-600">
                          {item.amount.toLocaleString()}
                        </div>
                      </div>
                      <div className="md:col-span-1">
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          disabled={cashFormData.items.length === 1}
                          className="p-2 text-slate-400 hover:text-red-500 transition-colors disabled:opacity-30"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex justify-end pt-4">
                  <div className="bg-slate-900 text-white px-8 py-4 rounded-2xl flex items-center gap-8 shadow-xl shadow-slate-900/20">
                    <span className="text-[10px] font-black uppercase tracking-widest opacity-60">Total Requested Amount</span>
                    <span className="text-2xl font-black">
                      TZS {cashFormData.items.reduce((sum, item) => sum + item.amount, 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-4 pt-8 border-t border-slate-50">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-6 py-4 border-2 border-slate-100 rounded-2xl font-bold text-slate-600 hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-4 bg-emerald-500 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-emerald-600 transition-all shadow-xl shadow-emerald-500/20 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
                  Submit Requisition
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default CashRequisitionModal;
